/**
 * Shippo live-rate mode toggle + parcel-dimensions form.
 *
 * Primitive layer (`.tondocs/skills/react-primitive-design.md` three-layer).
 * Controlled — owns no state; consumer holds `{ shippingMode, parcel }` and
 * forwards changes.
 *
 * Three consumers:
 *  - `components/publications/creation-flow/listing-creation/steps/shipping-step.tsx`
 *    (creation flow — WS3.1)
 *  - `components/my-listings/EditShippingTab.tsx` via
 *    `components/my-listings/edit-listing-dialog.tsx` (edit flow — WS3.2)
 *
 * Extracted 2026-06-19 (WS3.2.a) from the inline block in `shipping-step.tsx`.
 * Before extraction, creation and edit shared nothing above leaf primitives —
 * the canonical builder vs producer fork anti-pattern from Sibling-E.
 *
 * # Canonical-only emit (slice C2, 2026-06-23)
 *
 * `value.parcel` carries CANONICAL units only: weight in grams, dimensions in
 * millimetres (PLAN §A "Parcel units" policy C, locked 2026-06-22). The form
 * derives the DISPLAY locale unit from `sellerCountry` (US → oz + in; else →
 * kg + cm) and converts on every `onChange`. The two unit `<select>`s that
 * existed in slice C1 are gone — locale alone drives display.
 *
 * Each input is a `NumericInput` holding a locale-unit DRAFT string, with the
 * locale ⇄ canonical mapping injected as a codec
 * (`lib/shipping/parcel-locale-codec.ts`). A seller relocate (US → DE) swaps
 * the codec and re-renders the same canonical row in kg + cm.
 *
 * ## Typing fix, 2026-08-03
 *
 * Until 2026-08-03 the inputs were `type="number"` whose `value` was derived
 * from canonical state on every render. Because 1 in = 25.4 mm and mm are
 * stored as integers, that round trip was lossy and VISIBLE: typing `6` gave
 * `6 × 25.4 → 152 mm → 152 / 25.4 → "5.98"`. Typing `6.` was worse — the
 * decimal point vanished on the keystroke that created it (`Number("6.")` is
 * `6`), so no fractional dimension could be entered at all, and backspacing
 * hit the same wall. `type="number"` also reports invalid intermediate input
 * as `""`, silently clearing the field, and mutates on mouse-wheel scroll.
 *
 * The draft now lives in the input (see `components/ui/numeric-input.tsx`) and
 * the codec formats canonical values to the SHORTEST representation that
 * converts back exactly, so 152 mm renders as `6`, not `5.98`. The forked
 * conversion constants that used to live in this file are gone — `toGrams` /
 * `toMm` in `lib/shipping/normalize-parcel.ts` are the single source.
 *
 * `disabled` is reserved for the edit-flow "locked rate" guard (WS3.2.b):
 * when a `seller_orders` row references this listing with a non-terminal
 * `label_status` + `external_rate_id`, the seller cannot flip mode
 * retroactively — UI greys the surface, route returns the contract error.
 *
 * @see .tondocs/shippo/PLAN.md §WS3.2
 * @see .tondocs/shippo/PLAN.md §A row "Parcel units" (locked 2026-06-22)
 * @see lib/shipping/normalize-parcel.ts (canonical conversion factors)
 */
"use client"

import { useTranslations } from "next-intl"
import { useId, useMemo } from "react"
import { motion } from "framer-motion"
import { Check, PackageCheck } from "lucide-react"
import {
  MIN_PARCEL_HEIGHT_MM,
  MIN_PARCEL_LENGTH_MM,
  MIN_PARCEL_WEIGHT_GRAMS,
  MIN_PARCEL_WIDTH_MM,
} from "@kraahl/contract"
import { Switch } from "@/components/ui/switch"
import { NumericInput } from "@/components/ui/numeric-input"
import { Label } from "@/components/ui/label"
import {
  distanceCodecForUnit,
  localeUnitsForSeller,
  massCodecForUnit,
  type LocaleParcelUnits,
} from "@/lib/shipping/parcel-locale-codec"
import type { NumericFieldCodec } from "@/lib/forms/numeric-draft"
import {
  creationFlowBlockClass,
  creationFlowSectionLabelClass,
} from "@/lib/creation-flow-form-styles"
import { cn } from "@/lib/utils"
import type { SupportTier } from "@/lib/shipping/origin-support"

export type ShippingMode = "legacy" | "live_rate"

/**
 * Parcel dimensions as held by consumer state. CANONICAL units only (slice C2):
 * mass in grams, distances in millimetres. The form does locale ⇄ canonical
 * conversion internally; consumers persist these values verbatim into the
 * `parcel_weight_grams` / `parcel_{length,width,height}_mm` listing columns.
 */
export interface ParcelDimensions {
  /** Canonical — grams. */
  weight?: number
  /** Canonical — millimetres. */
  length?: number
  /** Canonical — millimetres. */
  width?: number
  /** Canonical — millimetres. */
  height?: number
}

export interface ShippingModeAndParcelValue {
  shippingMode: ShippingMode
  parcel: ParcelDimensions
}

export interface ShippingModeToggleAndParcelFormProps {
  value: ShippingModeAndParcelValue
  onChange: (next: ShippingModeAndParcelValue) => void
  /**
   * Seller's ISO-3166 alpha-2 country code (e.g. `'US'`). Required — drives
   * the locale unit displayed in inputs (US → oz + in; everywhere else → kg
   * + cm). Consumer reads this from `profiles.main_address.country` (or the
   * listing's pinned ship_from address). Stored as `string` rather than a
   * branded ISO type because the underlying column is `text`; we uppercase
   * on read so `'us'` / `'US'` both work.
   */
  sellerCountry: string
  compact?: boolean
  /**
   * When `true`, hides the toggle behind an explanatory note and disables
   * parcel editing. Used by edit-flow when a seller_order has a locked
   * rate in-flight — see WS3.2.b.
   */
  disabled?: boolean
  /**
   * Optional explanation rendered when `disabled` is `true` — typically the
   * "Cannot change shipping mode — orders are in-flight" copy from the
   * edit-flow.
   */
  disabledReason?: string
  /**
   * Per-origin support tier (WS3.1 extension, Option D — locked 2026-06-20).
   *
   * - `"broad"` (US): live_rate enabled, no warning.
   * - `"limited"` (GB/DE/FR): live_rate enabled with §5.2 disclosure copy.
   * - `"unsupported"` (CA/AU/anything not in `SHIPPO_ORIGIN_SUPPORT`):
   *   live_rate **disabled**; legacy is the only choice.
   * - `null` (seller has no main address yet): live_rate **disabled** with
   *   an address-setup prompt.
   *
   * The Container computes this: `getOriginSupport(country)?.tier ??
   * "unsupported"` when a country is present, `null` when not.
   *
   * Omit (or pass `undefined`) to render the legacy unconditional UI used
   * by surfaces that don't yet plumb the tier — e.g. chat-private listings.
   *
   * @see lib/shipping/origin-support.ts
   * @see .tondocs/shippo/ORIGIN_COUNTRY_SUPPORT.md §5.1
   */
  originTier?: SupportTier | null
  /**
   * Display name of the seller's origin country (e.g. "United Kingdom").
   * Used only when `originTier === "limited"` to interpolate the disclosure
   * copy. Falls back to the ISO code or "your country" if absent.
   */
  originCountryLabel?: string | null
  /**
   * Carrier currency-mismatch explainer — orthogonal axis to `originTier`.
   *
   * Set by the Container when `isCarrierAvailable(listing, 'shippo')` returns
   * `{ ok: false, reason: 'CURRENCY_MISMATCH' }` — i.e. seller's
   * `profile.currency` ≠ `native_currency(ship_from_country)` (the "Buffon
   * case": USD store shipping from ES). When non-null, the toggle is force-
   * disabled and the explainer is rendered below — irrespective of origin
   * tier (US-origin can still mismatch if the seller chose a non-USD currency).
   *
   * @see lib/shipping/carrier-eligibility.ts
   * @see .tondocs/payments/ADR_CURRENCY_PIN.md §"Carrier eligibility gate"
   */
  currencyMismatchExplainer?: string | null
}

/**
 * Display label for a locale unit. Always emit the typed unit string verbatim
 * (`oz` / `kg` / `in` / `cm`) so seller-facing strings interpolate as
 * "Minimum 6 in" / "Minimum 150 mm" without a separate i18n table. Mirrors
 * the existing `parcel-mass-unit` / `parcel-distance-unit` testid labels.
 */
function unitLabel(unit: "oz" | "kg" | "in" | "cm"): string {
  return unit
}

/**
 * Carrier-minimum gate (Option C UX, on top of Option A's publish-time Zod).
 * Compares a single canonical-unit value against the floor from
 * `@kraahl/contract/parcel-min-dims`. Returns the violation as a
 * locale-rendered floor string when red, or `null` when green / unset.
 *
 * `undefined` (cleared field) returns null — the seller hasn't typed yet,
 * so we don't surface an error. The publish-time Zod gate catches missing
 * required dims on submit.
 *
 * Pure function — no state, no side effects. Called inline from render.
 */
function violationForAxis(
  axis: "weight" | "length" | "width" | "height",
  canonicalValue: number | undefined,
  localeUnits: LocaleParcelUnits,
  codecs: { mass: NumericFieldCodec; distance: NumericFieldCodec },
): string | null {
  if (typeof canonicalValue !== "number") return null
  if (axis === "weight") {
    if (canonicalValue >= MIN_PARCEL_WEIGHT_GRAMS) return null
    // Render the floor with the SAME codec the input uses, so the number in
    // the error is one the seller can literally type to clear it. (The floors
    // are not on the imperial grid — 153 mm is 6.02 in — so this matters: an
    // error reading "6 in" would stay red after typing 6.)
    const floorLocale = codecs.mass.format(MIN_PARCEL_WEIGHT_GRAMS)
    return `${floorLocale} ${unitLabel(localeUnits.mass)}`
  }
  const floorMm =
    axis === "length"
      ? MIN_PARCEL_LENGTH_MM
      : axis === "width"
        ? MIN_PARCEL_WIDTH_MM
        : MIN_PARCEL_HEIGHT_MM
  if (canonicalValue >= floorMm) return null
  const floorLocale = codecs.distance.format(floorMm)
  return `${floorLocale} ${unitLabel(localeUnits.distance)}`
}

/**
 * All 4 canonical parcel dimensions present and positive (grams + mm).
 */
export function isParcelComplete(value: ShippingModeAndParcelValue): boolean {
  const { parcel } = value
  return (
    typeof parcel.weight === "number" && parcel.weight > 0 &&
    typeof parcel.length === "number" && parcel.length > 0 &&
    typeof parcel.width === "number" && parcel.width > 0 &&
    typeof parcel.height === "number" && parcel.height > 0
  )
}

/**
 * Render-time derivation of tier-driven UI state. Pure switch on
 * `SupportTier | null` with a `never`-probe so adding a fifth tier downstream
 * (per ORIGIN_COUNTRY_SUPPORT.md §5) is a one-file compile error here.
 *
 * `liveRateAvailable === false` greys/disables the toggle; `disclosure` is
 * inline copy rendered under the toggle (Limited disclosure §5.2,
 * Unsupported "not available", null "no main address").
 *
 * When `originTier` is `undefined` (legacy callsite), this returns `null` and
 * the Primitive renders its unconditional pre-tier behavior.
 */
function resolveTierUI(
  originTier: SupportTier | null | undefined,
  countryLabel: string | null | undefined,
): { liveRateAvailable: boolean; disclosure: string | null } | null {
  if (originTier === undefined) return null
  if (originTier === null) {
    return {
      liveRateAvailable: false,
      disclosure:
        "Add your main shipping address in Settings to enable live rates.",
    }
  }
  switch (originTier) {
    case "broad":
      return { liveRateAvailable: true, disclosure: null }
    case "limited": {
      const country = countryLabel?.trim() || "your country"
      return {
        liveRateAvailable: true,
        disclosure:
          `Limited carrier coverage from ${country}. Live shipping rates are routed through a single local carrier and may not cover all international destinations. ` +
          `If a buyer's address is unsupported, they will see "Shipping not available". Consider also enabling flat-rate methods for broader coverage.`,
      }
    }
    case "unsupported":
      return {
        liveRateAvailable: false,
        disclosure:
          "Live shipping rates aren't available from your country yet.",
      }
    default: {
      const _exhaustive: never = originTier
      return _exhaustive
    }
  }
}

/**
 * Fixed-height slot for one axis's carrier-minimum error.
 *
 * The message is rendered into a line that is ALWAYS present — invisible and
 * holding a non-breaking space when there is no violation. Conditionally
 * mounting the `<p>` (as this did until 2026-08-03) makes the 2×2 dimension
 * grid grow by a line the moment a value drops below a floor, so the row
 * beneath it jumps while the seller is still typing into the field above.
 *
 * `data-testid` and `role="alert"` stay CONDITIONAL even though the element
 * does not:
 *  - the testid is the e2e "no violation rendered" assertion
 *    (`toHaveCount(0)` in `tests/e2e/listing-create.spec.ts`), which an
 *    always-present node would break;
 *  - an empty `role="alert"` is a live region announcing nothing.
 *
 * One line is enough for every locale: the copy is `Minimum {floor}` (or its
 * translation) plus a short unit — see `parcelMinError` in `messages/*.json`.
 */
function ParcelFieldError({
  id,
  testId,
  message,
}: {
  id: string
  testId: string
  message: string | null
}) {
  return (
    <p
      id={id}
      role={message ? "alert" : undefined}
      data-testid={message ? testId : undefined}
      className={cn(
        "text-xs leading-4 min-h-4",
        message ? "text-destructive" : "invisible",
      )}
    >
      {/* NBSP, not a plain space: JSX collapses whitespace-only children,
          leaving the line with no height to reserve. */}
      {message ?? "\u00A0"}
    </p>
  )
}

export function ShippingModeToggleAndParcelForm({
  value,
  onChange,
  sellerCountry,
  compact: _compact = false,
  disabled = false,
  disabledReason,
  originTier,
  originCountryLabel,
  currencyMismatchExplainer,
}: ShippingModeToggleAndParcelFormProps) {
  const tLC = useTranslations("listingCreation")
  const { shippingMode, parcel } = value
  // Stable ids for `aria-describedby` wiring — one per axis. `useId`
  // generates SSR-safe unique ids per component instance so multiple
  // forms on a page don't collide.
  const errIdPrefix = useId()
  const errIds = {
    weight: `${errIdPrefix}-weight-err`,
    length: `${errIdPrefix}-length-err`,
    width: `${errIdPrefix}-width-err`,
    height: `${errIdPrefix}-height-err`,
  } as const
  const tierUI = resolveTierUI(originTier, originCountryLabel)
  // Tier gates live_rate availability. `disabled` (edit-flow lock) still
  // wins — the locked rate guard predates tier and applies regardless.
  // CURRENCY_MISMATCH is the third gate (orthogonal to tier): seller's
  // store currency ≠ native currency of their ship-from country.
  const tierAvailable = tierUI ? tierUI.liveRateAvailable : true
  const liveRateAvailable = tierAvailable && !currencyMismatchExplainer
  const switchDisabled = disabled || !liveRateAvailable
  const isLiveRate = shippingMode === "live_rate"
  const localeUnits = useMemo(() => localeUnitsForSeller(sellerCountry), [sellerCountry])

  // Locale ⇄ canonical mapping handed to each input. Memoised on the unit so
  // a seller relocate (US → DE) swaps both codecs and re-renders the same
  // canonical row in kg + cm. There is no separate "display" derivation any
  // more — `NumericInput` formats from `value` when it syncs, and otherwise
  // shows exactly what was typed.
  const codecs = useMemo(
    () => ({
      mass: massCodecForUnit(localeUnits.mass),
      distance: distanceCodecForUnit(localeUnits.distance),
    }),
    [localeUnits],
  )

  // Field-level carrier-minimum violations (Option C UX, 2026-06-30). Each
  // axis is checked independently in canonical units; the rendered floor is
  // converted back to locale so a US seller sees "Minimum 6 in", a DE seller
  // sees "Minimum 15 cm". Same SoT as the publish-time Zod gate
  // (`MIN_PARCEL_*` from `@kraahl/contract`) — no duplicate magic numbers.
  const violations = useMemo(
    () => ({
      weight: violationForAxis("weight", parcel.weight, localeUnits, codecs),
      length: violationForAxis("length", parcel.length, localeUnits, codecs),
      width: violationForAxis("width", parcel.width, localeUnits, codecs),
      height: violationForAxis("height", parcel.height, localeUnits, codecs),
    }),
    [parcel.weight, parcel.length, parcel.width, parcel.height, localeUnits, codecs],
  )

  const sectionLabel = creationFlowSectionLabelClass
  const cardRadius = "rounded-3xl"
  const numericInputClass = "bg-muted/80 text-foreground border-sheet-handle"
  const unitLabelClass =
    "text-xs font-semibold tracking-wide tabular-nums text-muted-foreground"

  const setMode = (next: ShippingMode) => {
    if (disabled) return
    // Tier gate: refuse a flip to live_rate when origin doesn't support it.
    // Flips away from live_rate (to legacy) are always allowed — they're
    // the safe path.
    if (next === "live_rate" && !liveRateAvailable) return
    onChange({ ...value, shippingMode: next })
  }

  /**
   * Store one canonical parcel field. `NumericInput` has already parsed the
   * draft through the codec, so the number arriving here is canonical (grams
   * / mm) and `undefined` means the seller cleared the field.
   *
   * No range check: a value below the carrier floor is still stored and shown
   * with its `violations` error. Refusing to store it would leave the input
   * and the state disagreeing — the seller would see a number the form does
   * not have — and would make a floor unreachable by typing through it.
   */
  const updateParcelField = (
    key: "weight" | "length" | "width" | "height",
    canonical: number | undefined,
  ) => {
    if (disabled) return
    onChange({ ...value, parcel: { ...parcel, [key]: canonical } })
  }

  return (
    <motion.div
      layout
      className={cn(
        cardRadius,
        "p-5 space-y-4 transition-colors relative overflow-hidden",
        isLiveRate
          ? "bg-gradient-to-br from-emerald-50 dark:from-emerald-950/40 via-sheet to-sheet border border-emerald-300 dark:border-emerald-500/40 ring-1 ring-emerald-100 dark:ring-emerald-500/15"
          : cn(creationFlowBlockClass, "border border-border"),
        disabled && "opacity-70",
      )}
      data-testid="shipping-mode-toggle-and-parcel-form"
    >
      <div className="flex items-start gap-3">
        <div className={cn(
          "shrink-0 rounded-2xl p-3 transition-colors",
          isLiveRate
            ? "bg-emerald-500/15 text-emerald-500"
            : "bg-muted text-foreground/80",
        )}>
          <PackageCheck className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <h3 className="text-base sm:text-lg font-semibold leading-tight text-foreground">
              {tLC("liveRateTitle")}
            </h3>
            {isLiveRate && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 shrink-0">
                <Check className="w-3 h-3" /> {tLC("liveRateActive")}
              </span>
            )}
          </div>
          <p className="text-sm leading-snug text-foreground/80">
            {tLC("liveRateSubtitle")}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {["USPS", "UPS", "FedEx", "DHL"].map((c) => (
          <span
            key={c}
            className="rounded-md px-2 py-0.5 text-[11px] font-semibold tracking-wide bg-muted text-foreground/80 border border-sheet-handle"
          >
            {c}
          </span>
        ))}
      </div>

      <ul className="space-y-1.5">
        {([tLC("liveRateBenefit1"), tLC("liveRateBenefit2"), tLC("liveRateBenefit3")] as const).map((b, i) => (
          <li
            key={i}
            className="flex items-start gap-2 text-sm text-foreground/90"
          >
            <Check
              className={cn(
                "w-4 h-4 mt-0.5 shrink-0",
                isLiveRate ? "text-emerald-500" : "text-muted-foreground",
              )}
            />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <p className="text-xs text-muted-foreground/70">
        {tLC("liveRateGeoNote")}
      </p>

      <div
        className={cn(
          "flex items-center justify-between gap-3 rounded-xl border px-4 py-3",
          isLiveRate
            ? "border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/70 dark:bg-emerald-950/30"
            : "border-sheet-handle bg-sheet/60",
        )}
      >
        <span
          className={cn(
            "text-sm font-semibold",
            isLiveRate
              ? "text-emerald-700 dark:text-emerald-300"
              : "text-foreground",
          )}
        >
          {isLiveRate ? tLC("liveRateActive") : tLC("liveRateActivate")}
        </span>
        <div>
          <Switch
            checked={isLiveRate}
            onCheckedChange={(checked) => setMode(checked ? "live_rate" : "legacy")}
            aria-label={tLC("liveRateActivate")}
            disabled={switchDisabled}
            aria-disabled={switchDisabled || undefined}
          />
        </div>
      </div>

      {disabled && disabledReason ? (
        <p
          className="text-xs text-amber-700 dark:text-amber-300"
          role="status"
        >
          {disabledReason}
        </p>
      ) : null}

      {tierUI?.disclosure ? (
        <p
          className="text-xs leading-snug text-foreground/80"
          role="status"
          data-testid="shipping-mode-tier-disclosure"
        >
          {tierUI.disclosure}
        </p>
      ) : null}

      {currencyMismatchExplainer ? (
        <p
          className="text-xs leading-snug text-amber-700 dark:text-amber-300"
          role="status"
          data-testid="shipping-mode-currency-mismatch"
        >
          {currencyMismatchExplainer}
        </p>
      ) : null}

      {isLiveRate ? (
        <motion.div
          layout
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3 pt-4 mt-1 border-t border-border"
        >
          <p className={cn(sectionLabel, "px-0.5")}>{tLC("parcelDimensions")}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-foreground/80">
                {tLC("parcelWeight")}
              </Label>
              <div className="flex items-center gap-2">
                <NumericInput
                  codec={codecs.mass}
                  className={cn(
                    numericInputClass,
                    violations.weight && "border-destructive",
                  )}
                  value={parcel.weight}
                  onValueChange={(next) => updateParcelField("weight", next)}
                  disabled={disabled}
                  aria-label={tLC("parcelWeight")}
                  aria-invalid={violations.weight ? true : undefined}
                  aria-describedby={
                    violations.weight ? errIds.weight : undefined
                  }
                />
                <span className={unitLabelClass} data-testid="parcel-mass-unit">
                  {localeUnits.mass}
                </span>
              </div>
              <ParcelFieldError
                id={errIds.weight}
                testId="parcel-weight-error"
                message={
                  violations.weight
                    ? tLC("parcelMinError", { floor: violations.weight })
                    : null
                }
              />
            </div>
            <div className="space-y-1">
              <Label className="text-foreground/80">
                {tLC("parcelLength")}
              </Label>
              <div className="flex items-center gap-2">
                <NumericInput
                  codec={codecs.distance}
                  className={cn(
                    numericInputClass,
                    violations.length && "border-destructive",
                  )}
                  value={parcel.length}
                  onValueChange={(next) => updateParcelField("length", next)}
                  disabled={disabled}
                  aria-label={tLC("parcelLength")}
                  aria-invalid={violations.length ? true : undefined}
                  aria-describedby={
                    violations.length ? errIds.length : undefined
                  }
                />
                <span className={unitLabelClass} data-testid="parcel-distance-unit">
                  {localeUnits.distance}
                </span>
              </div>
              <ParcelFieldError
                id={errIds.length}
                testId="parcel-length-error"
                message={
                  violations.length
                    ? tLC("parcelMinError", { floor: violations.length })
                    : null
                }
              />
            </div>
            <div className="space-y-1">
              <Label className="text-foreground/80">
                {tLC("parcelWidth")}
              </Label>
              <div className="flex items-center gap-2">
                <NumericInput
                  codec={codecs.distance}
                  className={cn(
                    numericInputClass,
                    violations.width && "border-destructive",
                  )}
                  value={parcel.width}
                  onValueChange={(next) => updateParcelField("width", next)}
                  disabled={disabled}
                  aria-label={tLC("parcelWidth")}
                  aria-invalid={violations.width ? true : undefined}
                  aria-describedby={
                    violations.width ? errIds.width : undefined
                  }
                />
                <span className={unitLabelClass}>{localeUnits.distance}</span>
              </div>
              <ParcelFieldError
                id={errIds.width}
                testId="parcel-width-error"
                message={
                  violations.width
                    ? tLC("parcelMinError", { floor: violations.width })
                    : null
                }
              />
            </div>
            <div className="space-y-1">
              <Label className="text-foreground/80">
                {tLC("parcelHeight")}
              </Label>
              <div className="flex items-center gap-2">
                <NumericInput
                  codec={codecs.distance}
                  className={cn(
                    numericInputClass,
                    violations.height && "border-destructive",
                  )}
                  value={parcel.height}
                  onValueChange={(next) => updateParcelField("height", next)}
                  disabled={disabled}
                  aria-label={tLC("parcelHeight")}
                  aria-invalid={violations.height ? true : undefined}
                  aria-describedby={
                    violations.height ? errIds.height : undefined
                  }
                />
                <span className={unitLabelClass}>{localeUnits.distance}</span>
              </div>
              <ParcelFieldError
                id={errIds.height}
                testId="parcel-height-error"
                message={
                  violations.height
                    ? tLC("parcelMinError", { floor: violations.height })
                    : null
                }
              />
            </div>
          </div>
        </motion.div>
      ) : null}
    </motion.div>
  )
}
